import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FaCheck, FaInbox, FaRedo } from "react-icons/fa";
import { useNotify } from "../../components/ConfirmModal";
import ItemDrawer from "./ItemDrawer";
import { AreaChip, Avatar, Empty, PageHead } from "./WorkUi";
import { errorMessage, fmtShortDate } from "./workApi";
import "../dm/dm.css";
import "./work.css";

function ReviewCard({ item, api, onDone, onOpen }) {
  const notify = useNotify();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const decide = async (decision) => {
    if (decision === "changes" && !note.trim()) {
      setError("Write what needs to change.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.review(item.Item_Id, decision, note.trim() || null);
      notify.success(decision === "approve" ? `Approved "${item.Title}"` : `Sent "${item.Title}" back for changes`);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const waited = item.Waiting_Hours == null ? null : item.Waiting_Hours >= 24 ? `${Math.floor(item.Waiting_Hours / 24)}d ${item.Waiting_Hours % 24}h` : `${item.Waiting_Hours}h`;

  return (
    <article className="dmp-card" aria-label={item.Title}>
      <div className="wk-detail-head" style={{ justifyContent: "space-between" }}>
        <button type="button" className="dmp-link" style={{ fontSize: 16, fontWeight: 700 }} onClick={() => onOpen(item.Item_Id)}>
          {item.Title}
        </button>
        {waited ? (
          <span className={`wk-count${item.Waiting_Hours >= 24 ? " wk-count--red" : ""}`}>Waiting {waited}</span>
        ) : null}
      </div>
      <div className="wk-card__meta" style={{ marginBottom: 10 }}>
        <span className="wk-card__project">{item.Project_Name}</span>
        {item.Tenant_Name ? <span className="wk-muted">· {item.Tenant_Name}</span> : null}
        <AreaChip name={item.Area_Name} color={item.Area_Color} icon={item.Area_Icon} />
        <span className="wk-muted">{item.Item_Type}</span>
        <span className="wk-card__who">
          <Avatar name={item.Executor_Name} size={22} /> {item.Executor_Name || "—"}
        </span>
      </div>
      {item.media?.length ? (
        <div className="wk-media-grid" style={{ marginBottom: 10 }}>
          {item.media.slice(0, 6).map((m, i) => (
            <div className="wk-media" key={m.URL || i}>
              {m.Kind === "video" ? (
                <video src={m.URL} controls preload="metadata" aria-label={m.Name || "Video"} />
              ) : m.Kind === "image" ? (
                <img src={m.URL} alt={m.Name || `Media ${i + 1}`} />
              ) : (
                <a href={m.URL} target="_blank" rel="noreferrer">
                  Open file
                </a>
              )}
            </div>
          ))}
        </div>
      ) : null}
      {item.Caption ? <div className="wk-pack-caption" style={{ marginBottom: 10 }}>{item.Caption}</div> : null}
      <div className="wk-muted" style={{ fontSize: 13, marginBottom: 8 }}>
        {item.Planned_Post_Date ? `Planned post: ${fmtShortDate(item.Planned_Post_Date)}${item.Platform ? ` on ${item.Platform}` : ""}` : null}
        {item.Due_Date ? ` · Due ${fmtShortDate(item.Due_Date)}` : null}
      </div>
      {error ? <div className="dmp-error">{error}</div> : null}
      <label className="wk-field">
        Feedback (required when asking for changes)
        <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Looks good / Please change…" />
      </label>
      <div className="wk-actions-bar" style={{ marginBottom: 0 }}>
        <button type="button" className="dmp-btn wk-btn-lg" onClick={() => decide("approve")} disabled={busy}>
          <FaCheck aria-hidden="true" /> Approve
        </button>
        <button type="button" className="dmp-btn dmp-btn--danger wk-btn-lg" onClick={() => decide("changes")} disabled={busy}>
          <FaRedo aria-hidden="true" /> Ask for changes
        </button>
      </div>
    </article>
  );
}

/** Everything waiting for my approval, oldest first. */
function ReviewInbox({ api }) {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [openItem, setOpenItem] = useState(() => Number(params.get("item")) || null);

  const load = useCallback(async () => {
    try {
      setData(await api.reviewInbox());
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load your reviews"));
    }
  }, [api]);

  useEffect(() => {
    load();
    api.meta().then(setMeta).catch(() => null);
  }, [api, load]);

  return (
    <div className="dmp wk">
      <PageHead title="Review Inbox">{data ? <span className="wk-count">{data.count} waiting</span> : null}</PageHead>
      {error ? <div className="dmp-error">{error}</div> : null}
      {!data ? (
        <div className="dmp-card wk-muted">Loading…</div>
      ) : data.items.length ? (
        data.items.map((item) => <ReviewCard key={item.Item_Id} item={item} api={api} onDone={load} onOpen={setOpenItem} />)
      ) : (
        <div className="dmp-card">
          <Empty icon={FaInbox} title="All caught up">
            Nothing is waiting for your review.
          </Empty>
        </div>
      )}
      {openItem ? <ItemDrawer api={api} itemId={openItem} meta={meta} onClose={() => setOpenItem(null)} onChanged={load} /> : null}
    </div>
  );
}

export default ReviewInbox;
